import { CalendarCompletedMark, CalendarPlannedMark } from './CalendarCompletedMark';
import { displayDigits } from '../utils/displayDigits';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { NmButton } from './NmButton';

export type NmPeaksWorkoutDot = 'run' | 'bike' | 'strength' | 'done' | 'planned' | 'missed' | 'default';

export interface NmPeaksCalendarDay {
  key: string;
  day: number | string;
  muted?: boolean;
  today?: boolean;
  selected?: boolean;
  workouts?: NmPeaksWorkoutDot[];
  hasCompletedWorkout?: boolean;
  hasPlannedWorkout?: boolean;
  isPast?: boolean;
  payload?: unknown;
}

export interface NmPeaksCalendarProps {
  title: string;
  subtitle?: string | number;
  days: NmPeaksCalendarDay[];
  legend?: { label: string; dot: NmPeaksWorkoutDot }[];
  onDayPress?: (day: NmPeaksCalendarDay) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onToday?: () => void;
  onDatePickerPress?: () => void;
  onUpload?: () => void;
  /** Embedded in calendar page toolbar — hide duplicate nav + legend, smaller grid. */
  compact?: boolean;
  /** Weekday column headers (7 items). Defaults to Persian Jalali abbreviations. */
  weekdayLabels?: string[];
  className?: string;
}

const FA_WEEKDAYS = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

const DOT_CLASS: Record<NmPeaksWorkoutDot, string> = {
  run: 'nm-peaks-cal__dot--run',
  planned: 'nm-peaks-cal__dot--planned',
  done: 'nm-peaks-cal__dot--done',
  bike: 'nm-peaks-cal__dot--bike',
  strength: 'nm-peaks-cal__dot--strength',
  missed: 'nm-peaks-cal__dot--missed',
  default: 'nm-peaks-cal__dot--planned',
};

/** Web port of VO2PeaksCalendarFa — Ai-Tempo / VO2 DS calendar month grid. */
export function NmPeaksCalendar({
  title,
  subtitle,
  days,
  legend,
  onDayPress,
  onPrevious,
  onNext,
  onToday,
  onDatePickerPress,
  onUpload,
  compact = false,
  weekdayLabels = FA_WEEKDAYS,
  className = '',
}: NmPeaksCalendarProps) {
  const { t, i18n } = useTranslation();
  const isRtl = /^(fa|ar)(-|$)/.test(i18n.language);
  const arrow = (right: boolean) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={right ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} />
    </svg>
  );
  const prevArrow = arrow(isRtl);
  const nextArrow = arrow(!isRtl);
  const showExtraNav = !compact && (onToday || onDatePickerPress || onUpload);

  return (
    <div
      className={`nm-peaks-cal${compact ? ' nm-peaks-cal--compact' : ''} ${className}`.trim()}
      dir={isRtl ? 'rtl' : 'ltr'}
    >
      <div className={`nm-peaks-cal__header${compact ? ' nm-peaks-cal__header--compact' : ''}`}>
        {compact ? (
          <>
            <NmButton
              type="button"
              variant="neutral"
              size="sm"
              className="nm-peaks-cal__nav-btn nm-peaks-cal__nav-btn--arrow"
              onClick={onPrevious}
              aria-label={t('calendar.nav.prevMonth', 'Previous month')}
            >
              {prevArrow}
            </NmButton>
            <div className="nm-peaks-cal__title-block nm-peaks-cal__title-block--center">
              <div className="nm-peaks-cal__title">
                {displayDigits(title)}
                {subtitle != null ? ` ${displayDigits(subtitle)}` : ''}
              </div>
            </div>
            <NmButton
              type="button"
              variant="neutral"
              size="sm"
              className="nm-peaks-cal__nav-btn nm-peaks-cal__nav-btn--arrow"
              onClick={onNext}
              aria-label={t('calendar.nav.nextMonth', 'Next month')}
            >
              {nextArrow}
            </NmButton>
          </>
        ) : (
          <>
            <div className="nm-peaks-cal__title-block">
              <div className="nm-peaks-cal__title">{displayDigits(title)}</div>
              {subtitle != null && <div className="nm-peaks-cal__subtitle">{displayDigits(subtitle)}</div>}
            </div>
            <div className="nm-peaks-cal__nav">
              <NmButton
                type="button"
                variant="neutral"
                size="sm"
                className="nm-peaks-cal__nav-btn"
                onClick={onPrevious}
                aria-label={t('calendar.nav.prevMonth', 'Previous month')}
              >
                {prevArrow}
              </NmButton>
              {onToday && (
                <NmButton type="button" variant="neutral" size="sm" className="nm-peaks-cal__nav-btn nm-peaks-cal__nav-btn--today" onClick={onToday}>
                  {t('calendar.nav.today', 'امروز')}
                </NmButton>
              )}
              <NmButton
                type="button"
                variant="neutral"
                size="sm"
                className="nm-peaks-cal__nav-btn"
                onClick={onNext}
                aria-label={t('calendar.nav.nextMonth', 'Next month')}
              >
                {nextArrow}
              </NmButton>
              {showExtraNav && onDatePickerPress && (
                <NmButton type="button" variant="neutral" size="sm" className="nm-peaks-cal__nav-btn" onClick={onDatePickerPress} aria-label={t('calendar.datePicker.title')}>
                  📅
                </NmButton>
              )}
              {showExtraNav && onUpload && (
                <NmButton type="button" variant="neutral" size="sm" className="nm-peaks-cal__nav-btn" onClick={onUpload} aria-label={t('calendar.upload')}>
                  ↑
                </NmButton>
              )}
            </div>
          </>
        )}
      </div>

      {/* Single 7-column grid — weekday labels share tracks with day cells for exact alignment. */}
      <div className="nm-peaks-cal__matrix">
        {weekdayLabels.map((wd, i) => (
          <span key={`wd-${i}`} className="nm-peaks-cal__weekday">
            {wd}
          </span>
        ))}
        {days.map((day, index) => {
          const isMuted = !!day.muted;
          const isSelected = !isMuted && !!day.selected;
          const hasWorkout = !isMuted && !isSelected && (day.workouts?.length ?? 0) > 0;
          const isToday = !!day.today && !isSelected;

          if (isSelected) {
            return (
              <button
                key={day.key || String(index)}
                type="button"
                className="nm-peaks-cal__cell nm-peaks-cal__cell--selected"
                onClick={() => onDayPress?.(day)}
              >
                <span className="nm-peaks-cal__day-num">{displayDigits(day.day)}</span>
                {(day.workouts?.length ?? 0) > 0 ? (
                  <span className="nm-peaks-cal__dots">
                    {day.hasCompletedWorkout && <CalendarCompletedMark />}
                    {(day.hasPlannedWorkout ?? day.workouts?.includes('planned')) && <CalendarPlannedMark missed={day.isPast} />}
                  </span>
                ) : null}
              </button>
            );
          }

          return (
            <button
              key={day.key || String(index)}
              type="button"
              disabled={isMuted}
              className={[
                'nm-peaks-cal__cell',
                isToday ? 'nm-peaks-cal__cell--today' : '',
                hasWorkout ? 'nm-peaks-cal__cell--has-workout' : '',
                isMuted ? 'nm-peaks-cal__cell--muted' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => onDayPress?.(day)}
            >
              <span className="nm-peaks-cal__day-num">{displayDigits(day.day)}</span>
              {!isMuted && (
                <span className="nm-peaks-cal__dots">
                  {day.hasCompletedWorkout && <CalendarCompletedMark />}
                  {(day.hasPlannedWorkout ?? day.workouts?.includes('planned')) && <CalendarPlannedMark missed={day.isPast} />}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {!compact && legend?.length ? (
        <div className="nm-peaks-cal__legend">
          {legend.map(({ label, dot }) => (
            <span key={label} className="nm-peaks-cal__legend-item">
              <span className={`nm-peaks-cal__dot ${DOT_CLASS[dot]}`} />
              {label}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}
